// ONYX-CUSTOM: background video and play/pause toggle behaviour
if (!customElements.get('video-background')) {
  customElements.define(
    'video-background',
    class VideoBackground extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;

        this.mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        this.toggle = this.dataset.toggle ? document.getElementById(this.dataset.toggle) : null;
        this.inView = false;
        // Set when play() is rejected, which is how iOS Low Power Mode shows up: there is no API for it.
        this.blocked = false;
        this.userPaused = FoxTheme.config.motionReduced || this.saveDataEnabled || this.dataset.autoplay === 'false';

        this.onBreakpoint = () => {
          if (!this.video.getAttribute('src')) return;
          this.load();
          this.play();
        };
        this.onPlaying = () => {
          this.blocked = false;
          this.classList.add('is-playing');
          this.sync();
        };
        this.onVisibility = () => {
          if (document.hidden) {
            this.video.pause();
          } else {
            this.play();
          }
        };
        this.onToggle = () => this.toggleUserPause();

        this.mql.addEventListener('change', this.onBreakpoint);
        this.video.addEventListener('playing', this.onPlaying);
        document.addEventListener('visibilitychange', this.onVisibility);

        if (this.toggle) {
          this.toggle.addEventListener('click', this.onToggle);
          this.toggle.hidden = false;
          this.sync();
        }

        this.stopInView = FoxTheme.Motion.inView(
          this,
          () => {
            this.inView = true;
            this.play();

            return () => {
              this.inView = false;
              this.video.pause();
            };
          },
          { margin: '100px 0px 100px 0px' }
        );
      }

      disconnectedCallback() {
        this.stopInView?.();
        this.mql?.removeEventListener('change', this.onBreakpoint);
        this.video?.removeEventListener('playing', this.onPlaying);
        this.toggle?.removeEventListener('click', this.onToggle);
        document.removeEventListener('visibilitychange', this.onVisibility);
        this.video?.pause();
      }

      get saveDataEnabled() {
        const connection = navigator.connection;
        return Boolean(connection && (connection.saveData || /2g$/.test(connection.effectiveType)));
      }

      get paused() {
        return this.userPaused || this.blocked;
      }

      get source() {
        return (this.mql.matches && this.dataset.srcMobile) || this.dataset.src;
      }

      get cutAt() {
        return this.mql.matches && this.dataset.srcMobile ? this.dataset.cutAtMobile : this.dataset.cutAt;
      }

      load() {
        const src = this.source;
        if (!src || this.video.getAttribute('src') === src) return;

        this.classList.remove('is-playing');
        this.video.muted = true;
        // Desktop and mobile can be different files with different cut points, so the active one goes on the
        // <video> itself, where assets/onyx-video-cut.js finds it first.
        if (this.cutAt) {
          this.video.dataset.onyxCutAt = this.cutAt;
        } else {
          delete this.video.dataset.onyxCutAt;
        }
        this.video.src = src;
      }

      async play() {
        if (this.paused || !this.inView || document.hidden) return;

        this.load();
        try {
          await this.video.play();
        } catch (error) {
          // AbortError is a pause() or source swap racing the request, not a block.
          if (error.name === 'NotAllowedError') {
            this.blocked = true;
            this.sync();
          }
        }
      }

      toggleUserPause() {
        if (this.paused) {
          this.userPaused = false;
          this.blocked = false;
          this.play();
        } else {
          this.userPaused = true;
          this.video.pause();
        }
        this.sync();
      }

      sync() {
        if (!this.toggle) return;

        const paused = this.paused;
        this.toggle.dataset.state = paused ? 'paused' : 'playing';
        this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
      }
    }
  );
}
